@if (isOpen) {
  <div class="dk-modal-overlay" (click)="onOverlayClick($event)" (keydown.escape)="onClose()">
    <div
      class="dk-modal settings-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settingsModalTitle"
      cdkTrapFocus
      [cdkTrapFocusAutoCapture]="true"
    >
      <h2 class="dk-visually-hidden" id="settingsModalTitle">Settings</h2>
      <button
        type="button"
        class="dk-modal__close settings-modal__close"
        (click)="onClose()"
        aria-label="Close settings"
      ></button>

      <nav class="settings-sidebar" aria-label="Settings sections">
        <div class="nav-group">
          <span class="nav-group-label">Account</span>
          <button
            type="button"
            class="nav-row"
            [attr.aria-current]="activeSection === 'account' ? 'page' : null"
            (click)="selectSection('account')"
          >
            <span class="dk-avatar dk-avatar--xs" aria-hidden="true">
              @if (userAvatar) {
                <img [src]="userAvatar" alt="" />
              } @else {
                {{ userInitials }}
              }
            </span>
            <span class="nav-row-label">{{ userName }}</span>
          </button>
          <button type="button" class="nav-row" disabled>
            <lucide-icon name="sliders-horizontal" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Preferences</span>
          </button>
          <button
            type="button"
            class="nav-row"
            [attr.aria-current]="activeSection === 'notifications' ? 'page' : null"
            (click)="selectSection('notifications')"
          >
            <lucide-icon name="bell" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Notifications</span>
          </button>
        </div>

        <div class="nav-group">
          <span class="nav-group-label">Workspace</span>
          <button
            type="button"
            class="nav-row"
            [attr.aria-current]="activeSection === 'workspace-general' ? 'page' : null"
            (click)="selectSection('workspace-general')"
          >
            <lucide-icon name="settings" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">General</span>
          </button>
          <button type="button" class="nav-row" disabled>
            <lucide-icon name="lock" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Security</span>
          </button>
        </div>

        @if (projects.length > 0) {
          <div class="nav-group">
            <span class="nav-group-label">Projects</span>
            <button
              type="button"
              class="nav-row"
              [attr.aria-current]="activeSection === 'projects-general' ? 'page' : null"
              (click)="selectSection('projects-general')"
            >
              <lucide-icon name="lock" [size]="15" class="nav-ic" aria-hidden="true" />
              <span class="nav-row-label">General</span>
            </button>
          </div>
        }

        <div class="nav-group">
          <span class="nav-group-label">Features</span>
          <button type="button" class="nav-row" disabled>
            <lucide-icon name="gem" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Connections</span>
          </button>
          <button type="button" class="nav-row" disabled>
            <lucide-icon name="package" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Declunk MCP</span>
          </button>
          <button
            type="button"
            class="nav-row"
            [attr.aria-current]="activeSection === 'agents' ? 'page' : null"
            (click)="selectSection('agents')"
          >
            <lucide-icon name="sparkles" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Agents</span>
          </button>
        </div>

        <div class="nav-group">
          <span class="nav-group-label">Billing</span>
          <button type="button" class="nav-row" disabled>
            <lucide-icon name="credit-card" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Billing</span>
          </button>
          <button type="button" class="nav-row" disabled>
            <lucide-icon name="circle-check" [size]="15" class="nav-ic" aria-hidden="true" />
            <span class="nav-row-label">Plans</span>
          </button>
        </div>

        @if (!isProd) {
          <div class="nav-group">
            <span class="nav-group-label">Design System</span>
            <a routerLink="/showcase" class="nav-row">
              <lucide-icon name="swatch-book" [size]="15" class="nav-ic" aria-hidden="true" />
              <span class="nav-row-label">Showcase</span>
            </a>
          </div>
        }
      </nav>

      <div class="settings-content" #settingsContent>
        @if (activeSection === 'account') {
          <h1 class="dk-heading-3 settings-page-title">Account</h1>

          <section class="settings-section">
            <h2 class="dk-heading-4">Profile</h2>
            <hr class="dk-divider" />

            <div class="profile-row">
              <div class="icon-edit">
                <span class="dk-avatar dk-avatar--xl icon-edit__image" [attr.aria-busy]="isUploadingAvatar || null">
                  @if (userAvatar) {
                    <img [src]="userAvatar" alt="" />
                  } @else {
                    {{ userInitials }}
                  }
                </span>
                <button
                  type="button"
                  class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--icon dk-btn--sm icon-edit__btn"
                  (click)="avatarInput.click()"
                  [disabled]="isUploadingAvatar"
                  aria-label="Change profile picture"
                >
                  <lucide-icon name="camera" [size]="14" aria-hidden="true" />
                </button>
                <input #avatarInput type="file" accept="image/*" hidden (change)="onAvatarSelected($event)" />
              </div>

              <div class="dk-form-group">
                <label class="dk-form-group__label" for="settingsFirstName">First name</label>
                <input
                  class="dk-field"
                  id="settingsFirstName"
                  type="text"
                  [(ngModel)]="firstName"
                  (blur)="saveName('first_name')"
                />
              </div>

              <div class="dk-form-group">
                <label class="dk-form-group__label" for="settingsLastName">Last name</label>
                <input
                  class="dk-field"
                  id="settingsLastName"
                  type="text"
                  [(ngModel)]="lastName"
                  (blur)="saveName('last_name')"
                />
              </div>
            </div>
          </section>

          <section class="settings-section">
            <h2 class="dk-heading-4">Security</h2>
            <hr class="dk-divider" />

            <div class="info-row">
              <p class="dk-body-sm dk-font-medium">Email</p>
              <p class="dk-body-sm">{{ userEmail }}</p>
            </div>

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium">Password</p>
                <p class="dk-caption">Change password for your account</p>
              </div>
              <button
                type="button"
                class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm"
                [attr.aria-expanded]="passwordFormOpen"
                (click)="togglePasswordForm()"
              >
                Change password
              </button>
            </div>

            @if (passwordFormOpen) {
              <div class="password-form">
                <div class="dk-form-group">
                  <label class="dk-form-group__label" for="currentPassword">Current password</label>
                  <input
                    class="dk-field"
                    id="currentPassword"
                    type="password"
                    autocomplete="current-password"
                    [(ngModel)]="currentPassword"
                  />
                </div>
                <div class="dk-form-group">
                  <label class="dk-form-group__label" for="newPassword">New password</label>
                  <input
                    class="dk-field"
                    id="newPassword"
                    type="password"
                    autocomplete="new-password"
                    [(ngModel)]="newPassword"
                  />
                </div>
                <div class="dk-form-group">
                  <label class="dk-form-group__label" for="confirmPassword">Confirm new password</label>
                  <input
                    class="dk-field"
                    id="confirmPassword"
                    type="password"
                    autocomplete="new-password"
                    [(ngModel)]="confirmPassword"
                  />
                </div>
                <div class="password-form__actions">
                  <button type="button" class="dk-btn dk-btn--ghost dk-btn--secondary" (click)="togglePasswordForm()">
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="dk-btn"
                    [disabled]="passwordSubmitting"
                    [attr.data-loading]="passwordSubmitting ? '' : null"
                    [attr.aria-busy]="passwordSubmitting"
                    (click)="submitPasswordChange()"
                  >
                    Save
                  </button>
                </div>
              </div>
            }
          </section>

          <section class="settings-section">
            <h2 class="dk-heading-4">Support</h2>
            <hr class="dk-divider" />

            <div class="dk-card danger-zone">
              <div class="danger-zone__row">
                <div>
                  <p class="dk-body-sm dk-font-medium">Delete my account</p>
                  <p class="dk-caption">
                    Permanently delete your account. You'll no longer be able to access your projects or any of the
                    projects you belong to.
                  </p>
                </div>
                <button type="button" class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm">
                  Delete my account
                </button>
              </div>

              <div class="danger-zone__row">
                <div>
                  <p class="dk-body-sm dk-font-medium">Log out of all devices</p>
                  <p class="dk-caption">Log out of active sessions on all your devices, other than this one</p>
                </div>
                <button type="button" class="dk-btn dk-btn--destructive dk-btn--sm">Log out of all</button>
              </div>
            </div>
          </section>
        }

        @if (activeSection === 'notifications') {
          <h1 class="dk-heading-3 settings-page-title">Notifications</h1>

          @if (notifPrefsLoading) {
            <p class="dk-caption settings-status" role="status">Loading settings…</p>
          } @else if (notifPrefsError) {
            <div class="dk-alert dk-alert--destructive" role="alert">
              <div class="dk-alert__body">Couldn't load settings. Try again.</div>
            </div>
          } @else {
            @for (group of groupedSettingsPrefs; track group.label; let gi = $index) {
              <section class="settings-section">
                <div class="notif-section-head">
                  <h2 class="dk-heading-4">{{ group.label }}</h2>
                  @if (gi === 0) {
                    <div class="notif-pref-header" aria-hidden="true">
                      <span class="dk-label">In-app</span>
                      <span class="dk-label">Email</span>
                    </div>
                  }
                </div>
                <hr class="dk-divider" />

                @for (pref of group.prefs; track pref.type) {
                  <div class="notif-pref-row">
                    <div>
                      <p class="dk-body-sm dk-font-medium">{{ pref.label }}</p>
                      <p class="dk-caption">{{ pref.description }}</p>
                    </div>
                    <input
                      class="dk-switch"
                      type="checkbox"
                      role="switch"
                      [checked]="pref.enabled"
                      (change)="togglePref(pref)"
                      [attr.aria-label]="pref.label + ' in-app'"
                    />
                    <input
                      class="dk-checkbox"
                      type="checkbox"
                      [checked]="pref.emailEnabled"
                      (change)="toggleEmail(pref)"
                      [attr.aria-label]="pref.label + ' email'"
                    />
                  </div>
                }
              </section>
            }
          }
        }

        @if (activeSection === 'agents') {
          <h1 class="dk-heading-3 settings-page-title">Agents</h1>
          <app-agent-credentials [organizationId]="organizationId" />
        }

        @if (activeSection === 'workspace-general') {
          <h1 class="dk-heading-3 settings-page-title">Workspace settings</h1>
          <hr class="dk-divider" />

          <section class="settings-section">
            <h2 class="dk-heading-4">Icon</h2>

            <div class="profile-row">
              <div class="icon-edit">
                <span
                  class="settings-icon icon-edit__image"
                  [class.settings-icon--empty]="!workspaceIcon"
                  [attr.aria-busy]="isUploadingIcon || null"
                >
                  @if (workspaceIcon) {
                    <img [src]="workspaceIcon" alt="" />
                  } @else {
                    {{ workspaceInitials }}
                  }
                </span>
                <button
                  type="button"
                  class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--icon dk-btn--sm icon-edit__btn"
                  (click)="workspaceIconInput.click()"
                  [disabled]="isUploadingIcon"
                  aria-label="Change workspace icon"
                >
                  <lucide-icon name="camera" [size]="14" aria-hidden="true" />
                </button>
                <input
                  #workspaceIconInput
                  type="file"
                  accept="image/*"
                  hidden
                  (change)="onWorkspaceIconSelected($event)"
                />
              </div>

              <div class="dk-form-group">
                <label class="dk-form-group__label" for="workspaceName">Workspace name</label>
                <input
                  class="dk-field"
                  id="workspaceName"
                  type="text"
                  [(ngModel)]="workspaceName"
                  (blur)="saveWorkspaceName()"
                />
              </div>
            </div>
          </section>

          <section class="settings-section">
            <h2 class="dk-heading-4">Team &amp; roles</h2>
            <hr class="dk-divider" />

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="wsLinkJoinLabel">Add members via link</p>
                <p class="dk-caption" id="wsLinkJoinHint">
                  Only people with permission to invite members to this workspace can view this link.
                </p>
              </div>
              <div class="row-actions">
                <button
                  type="button"
                  class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm"
                  [disabled]="!linkJoinEnabled || !workspaceInvitationCode"
                  (click)="copyWorkspaceInviteLink()"
                >
                  Copy link
                </button>
                <input
                  class="dk-switch"
                  type="checkbox"
                  role="switch"
                  [checked]="linkJoinEnabled"
                  [disabled]="domainJoinLoading || isUpdatingLinkJoin"
                  (change)="toggleLinkJoin()"
                  aria-labelledby="wsLinkJoinLabel"
                  aria-describedby="wsLinkJoinHint"
                />
              </div>
            </div>

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="wsDomainJoinLabel">Join workspace</p>
                <p class="dk-caption" id="wsDomainJoinHint">
                  Let anyone with an <strong>&#64;company</strong> email join this workspace.
                </p>
              </div>
              <input
                class="dk-switch"
                type="checkbox"
                role="switch"
                [checked]="allowDomainJoin"
                [disabled]="domainJoinLoading || isUpdatingDomainJoin"
                (change)="toggleDomainJoin()"
                aria-labelledby="wsDomainJoinLabel"
                aria-describedby="wsDomainJoinHint"
              />
            </div>

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="wsTimeTrackingLabel">Time tracking</p>
                <p class="dk-caption" id="wsTimeTrackingHint">
                  Control whether members can log hours on requests across every project in this workspace.
                </p>
              </div>
              <dk-select
                class="row-select"
                size="sm"
                ariaLabelledBy="wsTimeTrackingLabel"
                describedBy="wsTimeTrackingHint"
                [options]="timeTrackingOptions"
                [ngModel]="timeTracking"
                (ngModelChange)="onTimeTrackingChange($event)"
                [disabled]="domainJoinLoading || isUpdatingTimeTracking || !canEditTimeTracking"
              />
            </div>

            <h3 class="dk-body-sm dk-font-semibold members-title">Members</h3>

            @if (membersTableLoading) {
              <p class="dk-caption settings-status" role="status">Loading members…</p>
            } @else if (membersError) {
              <div class="dk-alert dk-alert--destructive" role="alert">
                <div class="dk-alert__body">Couldn't load members. Try again.</div>
              </div>
            } @else {
              <div class="dk-table-wrap" tabindex="0" role="region" aria-label="Workspace members">
                <table class="dk-table dk-table--compact">
                  <caption class="dk-visually-hidden">
                    Workspace members
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Role</th>
                      <th scope="col" class="dk-table__num">Projects involved</th>
                      <th scope="col">Last active</th>
                      <th scope="col"><span class="dk-visually-hidden">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (member of pagedMembers; track member.id) {
                      <tr>
                        <td>
                          <div class="member-cell">
                            <span class="dk-avatar" aria-hidden="true">{{ member.name.slice(0, 2) }}</span>
                            <div class="member-cell__text">
                              <p class="dk-font-medium">{{ member.name }}</p>
                              <p class="dk-caption">{{ member.email }}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          @if (
                            canEditOrgMemberRoles && member.role && member.role !== 'owner' && orgRoleOptions.length
                          ) {
                            <select
                              class="dk-field dk-field--select dk-field--sm member-role-select"
                              [attr.aria-label]="'Role for ' + member.name"
                              [ngModel]="orgMemberRoleOptionId(member)"
                              [disabled]="isSavingOrgMemberRole(member.id) || orgRolesLoading"
                              (ngModelChange)="onOrgMemberRoleChange(member, $event)"
                            >
                              @for (option of orgRoleOptions; track option.id) {
                                <option [value]="option.id">{{ option.label }}</option>
                              }
                            </select>
                          } @else {
                            <span class="member-role">{{ member.role ?? '—' }}</span>
                          }
                        </td>
                        <td class="dk-table__num">{{ member.projects_involved }}</td>
                        <td>{{ member.last_active ? (member.last_active | date) : '—' }}</td>
                        <td>
                          <button
                            type="button"
                            class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                            disabled
                            [attr.aria-label]="'Actions for ' + member.name"
                          >
                            <lucide-icon name="ellipsis-vertical" [size]="16" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <div class="members-pagination">
                <span class="dk-caption">{{ memberPageRangeLabel }}</span>
                <div class="members-pagination__controls">
                  <select
                    class="dk-field dk-field--select dk-field--sm members-pagination__size"
                    aria-label="Members per page"
                    [ngModel]="pageSize"
                    (ngModelChange)="setPageSize($event)"
                  >
                    @for (size of pageSizeOptions; track size) {
                      <option [value]="size">{{ size }} per page</option>
                    }
                  </select>
                  <nav class="members-pagination__pages" aria-label="Workspace members pages">
                    <button
                      type="button"
                      class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                      [disabled]="currentPage === 1"
                      (click)="setPage(currentPage - 1)"
                      aria-label="Previous page"
                    >
                      <lucide-icon name="chevron-left" [size]="16" aria-hidden="true" />
                    </button>
                    @for (page of memberPageNumbers; track page) {
                      <button
                        type="button"
                        class="dk-btn dk-btn--icon dk-btn--sm"
                        [class.dk-btn--ghost]="page !== currentPage"
                        [class.dk-btn--secondary]="page !== currentPage"
                        [attr.aria-current]="page === currentPage ? 'page' : null"
                        [attr.aria-label]="'Page ' + page"
                        (click)="setPage(page)"
                      >
                        {{ page }}
                      </button>
                    }
                    <button
                      type="button"
                      class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                      [disabled]="currentPage === totalMemberPages"
                      (click)="setPage(currentPage + 1)"
                      aria-label="Next page"
                    >
                      <lucide-icon name="chevron-right" [size]="16" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            }
          </section>

          <section class="settings-section">
            <div class="dk-card danger-zone">
              <h2 class="dk-heading-4">Danger zone</h2>

              <div class="danger-zone__row">
                <div>
                  <p class="dk-body-sm dk-font-medium">Leave workspace</p>
                  <p class="dk-caption">
                    Remove your account from this workspace. You'll lose access to the workspace and all of its content,
                    including your projects.
                  </p>
                </div>
                <button type="button" class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm">
                  Leave workspace
                </button>
              </div>

              <div class="danger-zone__row">
                <div>
                  <p class="dk-body-sm dk-font-medium">Delete workspace</p>
                  <p class="dk-caption">Permanently delete this workspace, including all projects</p>
                </div>
                <button type="button" class="dk-btn dk-btn--destructive dk-btn--sm">Delete workspace</button>
              </div>
            </div>
          </section>
        }

        @if (activeSection === 'projects-general' && projects.length > 0) {
          <div class="project-switcher-row" role="group" aria-label="Projects">
            @for (project of projects; track project.id) {
              <button
                type="button"
                class="project-switcher"
                [attr.aria-pressed]="project.id === selectedProjectId"
                [style.background]="project.logo ? 'transparent' : project.color"
                (click)="selectProject(project)"
                [attr.aria-label]="project.name"
                [dkTooltip]="project.name"
                dkTooltipPosition="below"
              >
                @if (project.logo) {
                  <img [src]="project.logo" alt="" />
                } @else {
                  {{ project.name.slice(0, 2) }}
                }
              </button>
            }
          </div>

          <h1 class="dk-heading-3 settings-page-title">{{ selectedProjectName }} settings</h1>

          <section class="settings-section">
            <h2 class="dk-heading-4">Icon</h2>
            <hr class="dk-divider" />

            <div class="profile-row">
              <div class="icon-edit">
                <span
                  class="settings-icon icon-edit__image"
                  [class.settings-icon--empty]="!projectIcon"
                  [attr.aria-busy]="isUploadingProjectIcon || null"
                >
                  @if (projectIcon) {
                    <img [src]="projectIcon" alt="" />
                  } @else {
                    {{ projectInitials }}
                  }
                </span>
                <button
                  type="button"
                  class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--icon dk-btn--sm icon-edit__btn"
                  (click)="projectIconInput.click()"
                  [disabled]="isUploadingProjectIcon || !isProjectAdmin"
                  aria-label="Change project icon"
                >
                  <lucide-icon name="camera" [size]="14" aria-hidden="true" />
                </button>
                <input #projectIconInput type="file" accept="image/*" hidden (change)="onProjectIconSelected($event)" />
              </div>

              <div class="dk-form-group">
                <label class="dk-form-group__label" for="projectName">Project name</label>
                <input
                  class="dk-field"
                  id="projectName"
                  type="text"
                  [(ngModel)]="projectName"
                  [disabled]="!isProjectAdmin"
                  (blur)="saveProjectName()"
                />
              </div>
            </div>
          </section>

          <section class="settings-section">
            <h2 class="dk-heading-4">Project team &amp; roles</h2>
            <hr class="dk-divider" />

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="projLinkJoinLabel">
                  Invite members to {{ selectedProjectName }} project
                </p>
                <p class="dk-caption" id="projLinkJoinHint">
                  Share a link to invite people to <strong>{{ selectedProjectName }} project</strong> outside the
                  company workspace.
                </p>
              </div>
              <div class="row-actions">
                <button
                  type="button"
                  class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm"
                  [disabled]="!projectLinkJoinEnabled || !projectInvitationCode"
                  (click)="copyProjectInviteLink()"
                >
                  Copy link
                </button>
                <button
                  type="button"
                  class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm"
                  [disabled]="!projectLinkJoinEnabled || isRegeneratingProjectLink || !isProjectAdmin"
                  [attr.data-loading]="isRegeneratingProjectLink ? '' : null"
                  [attr.aria-busy]="isRegeneratingProjectLink"
                  (click)="regenerateProjectInviteLink()"
                >
                  Regenerate
                </button>
                <input
                  class="dk-switch"
                  type="checkbox"
                  role="switch"
                  [checked]="projectLinkJoinEnabled"
                  [disabled]="isUpdatingProjectLinkJoin || projectDetailLoading || !isProjectAdmin"
                  (change)="toggleProjectLinkJoin()"
                  aria-labelledby="projLinkJoinLabel"
                  aria-describedby="projLinkJoinHint"
                />
              </div>
            </div>

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="projAutoExpireLabel">Auto-expire invite links</p>
                <p class="dk-caption" id="projAutoExpireHint">
                  Invite members will be assigned this role when they join.
                </p>
              </div>
              <input
                class="dk-switch"
                type="checkbox"
                role="switch"
                [checked]="autoExpireInviteLinks"
                (change)="autoExpireInviteLinks = !autoExpireInviteLinks"
                aria-labelledby="projAutoExpireLabel"
                aria-describedby="projAutoExpireHint"
              />
            </div>

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="projAllowInviteLabel">Allow members to invite others</p>
                <p class="dk-caption" id="projAllowInviteHint">Let member roles send invitations</p>
              </div>
              <input
                class="dk-switch"
                type="checkbox"
                role="switch"
                [checked]="allowMembersToInvite"
                (change)="allowMembersToInvite = !allowMembersToInvite"
                aria-labelledby="projAllowInviteLabel"
                aria-describedby="projAllowInviteHint"
              />
            </div>

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="projDefaultRoleLabel">Default role for new members</p>
                <p class="dk-caption" id="projDefaultRoleHint">
                  This role will be assigned to anyone who joins {{ selectedProjectName }} via the invite link.
                </p>
              </div>
              <select
                class="dk-field dk-field--select dk-field--sm row-select"
                aria-labelledby="projDefaultRoleLabel"
                aria-describedby="projDefaultRoleHint"
                [ngModel]="projectDefaultRole"
                (ngModelChange)="onProjectDefaultRoleChange($event)"
                [disabled]="isSavingProjectDefaultRole || projectDetailLoading || !isProjectAdmin"
              >
                @for (option of projectDefaultRoleOptions; track option.id) {
                  <option [value]="option.id">{{ option.label }}</option>
                }
              </select>
            </div>

            <div class="info-row info-row--with-action">
              <div>
                <p class="dk-body-sm dk-font-medium" id="projAssigneeLabel">Default request assignee</p>
                <p class="dk-caption" id="projAssigneeHint">
                  This user will be automatically assigned whenever a new request is created.
                </p>
              </div>
              <div class="dk-field-wrap row-select">
                @if (defaultAssigneeMember) {
                  <span class="dk-field-wrap__addon dk-field-wrap__addon--start" aria-hidden="true">
                    <span class="dk-avatar dk-avatar--xs">
                      @if (defaultAssigneeMember.avatar) {
                        <img [src]="defaultAssigneeMember.avatar" alt="" />
                      } @else {
                        {{ defaultAssigneeMember.first_name[0] + defaultAssigneeMember.last_name[0] }}
                      }
                    </span>
                  </span>
                }
                <select
                  class="dk-field dk-field--select dk-field--sm"
                  aria-labelledby="projAssigneeLabel"
                  aria-describedby="projAssigneeHint"
                  [ngModel]="defaultAssigneeId"
                  (ngModelChange)="setDefaultAssignee($event)"
                  [disabled]="isSavingDefaultAssignee || projectDetailLoading || !isProjectAdmin"
                >
                  <option [ngValue]="null">Unassigned</option>
                  @for (member of activeProjectMembers; track member.id) {
                    <option [ngValue]="member.user_id">{{ member.first_name }} {{ member.last_name }}</option>
                  }
                </select>
              </div>
            </div>

            <h3 class="dk-body-sm dk-font-semibold members-title">Members</h3>

            @if (projectMembersTableLoading) {
              <p class="dk-caption settings-status" role="status">Loading members…</p>
            } @else if (projectMembersError) {
              <div class="dk-alert dk-alert--destructive" role="alert">
                <div class="dk-alert__body">Couldn't load members. Try again.</div>
              </div>
            } @else {
              <div class="dk-table-wrap" tabindex="0" role="region" aria-label="Project members">
                <table class="dk-table dk-table--compact">
                  <caption class="dk-visually-hidden">
                    Project members
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Role</th>
                      <th scope="col" class="dk-table__num">Projects involved</th>
                      <th scope="col">Last active</th>
                      <th scope="col"><span class="dk-visually-hidden">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (member of pagedProjectMembers; track member.id) {
                      <tr>
                        <td>
                          <div class="member-cell">
                            <span class="dk-avatar" aria-hidden="true">{{ member.name.slice(0, 2) }}</span>
                            <div class="member-cell__text">
                              <p class="dk-font-medium">{{ member.name }}</p>
                              <p class="dk-caption">{{ member.email }}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          @if (canEditMemberRoles && member.role && projectRoleOptions.length) {
                            <select
                              class="dk-field dk-field--select dk-field--sm member-role-select"
                              [attr.aria-label]="'Role for ' + member.name"
                              [ngModel]="memberRoleOptionId(member)"
                              [disabled]="isSavingMemberRole(member.id) || projectRolesLoading"
                              (ngModelChange)="onMemberRoleChange(member, $event)"
                            >
                              @for (option of projectRoleOptions; track option.id) {
                                <option [value]="option.id">{{ option.label }}</option>
                              }
                            </select>
                          } @else {
                            <span class="member-role">{{ member.role ?? '—' }}</span>
                          }
                        </td>
                        <td class="dk-table__num">{{ member.projects_involved }}</td>
                        <td>{{ member.last_active ? (member.last_active | date) : '—' }}</td>
                        <td>
                          @if (canRemoveProjectMember(member)) {
                            <button
                              type="button"
                              class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                              cdkOverlayOrigin
                              #memberMenuTrigger="cdkOverlayOrigin"
                              (click)="toggleMemberMenu(member.id)"
                              [attr.aria-label]="'Actions for ' + member.name"
                              aria-haspopup="menu"
                              [attr.aria-expanded]="openMemberMenuId === member.id"
                            >
                              <lucide-icon name="ellipsis-vertical" [size]="16" aria-hidden="true" />
                            </button>
                            <ng-template
                              cdkConnectedOverlay
                              [cdkConnectedOverlayOrigin]="memberMenuTrigger"
                              [cdkConnectedOverlayOpen]="openMemberMenuId === member.id"
                              [cdkConnectedOverlayPositions]="memberMenuPositions"
                              cdkConnectedOverlayPanelClass="dk-overlay--popover"
                              (overlayOutsideClick)="
                                onMemberMenuOutsideClick($event, memberMenuTrigger.elementRef.nativeElement)
                              "
                              (overlayKeydown)="onMemberMenuKeydown($event)"
                              (detach)="closeMemberMenu()"
                            >
                              <div class="dk-popover" cdkTrapFocus [cdkTrapFocusAutoCapture]="true">
                                <div
                                  class="dk-listbox dk-listbox--menu"
                                  role="menu"
                                  [attr.aria-label]="'Actions for ' + member.name"
                                >
                                  <button
                                    type="button"
                                    class="dk-option dk-text-destructive"
                                    role="menuitem"
                                    (click)="requestRemoveMember(member)"
                                  >
                                    Remove member
                                  </button>
                                </div>
                              </div>
                            </ng-template>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <div class="members-pagination">
                <span class="dk-caption">{{ projectMemberPageRangeLabel }}</span>
                <div class="members-pagination__controls">
                  <select
                    class="dk-field dk-field--select dk-field--sm members-pagination__size"
                    aria-label="Members per page"
                    [ngModel]="projectMemberPageSize"
                    (ngModelChange)="setProjectMemberPageSize($event)"
                  >
                    @for (size of pageSizeOptions; track size) {
                      <option [value]="size">{{ size }} per page</option>
                    }
                  </select>
                  <nav class="members-pagination__pages" aria-label="Project members pages">
                    <button
                      type="button"
                      class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                      [disabled]="projectMemberCurrentPage === 1"
                      (click)="setProjectMemberPage(projectMemberCurrentPage - 1)"
                      aria-label="Previous page"
                    >
                      <lucide-icon name="chevron-left" [size]="16" aria-hidden="true" />
                    </button>
                    @for (page of projectMemberPageNumbers; track page) {
                      <button
                        type="button"
                        class="dk-btn dk-btn--icon dk-btn--sm"
                        [class.dk-btn--ghost]="page !== projectMemberCurrentPage"
                        [class.dk-btn--secondary]="page !== projectMemberCurrentPage"
                        [attr.aria-current]="page === projectMemberCurrentPage ? 'page' : null"
                        [attr.aria-label]="'Page ' + page"
                        (click)="setProjectMemberPage(page)"
                      >
                        {{ page }}
                      </button>
                    }
                    <button
                      type="button"
                      class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                      [disabled]="projectMemberCurrentPage === projectMemberTotalPages"
                      (click)="setProjectMemberPage(projectMemberCurrentPage + 1)"
                      aria-label="Next page"
                    >
                      <lucide-icon name="chevron-right" [size]="16" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            }
          </section>

          <section class="settings-section" id="integrationsSection">
            <h2 class="dk-heading-4">Integrations</h2>
            <hr class="dk-divider" />

            <div class="integration-row">
              <div class="integration-row__text">
                <p class="dk-body-sm dk-font-medium integration-row__title">
                  Webflow
                  @if (webflowConnected && webflowConnectedSince) {
                    <span class="dk-caption">Connected since {{ webflowConnectedSince }}</span>
                  }
                </p>
                <p class="dk-caption">Connect your Webflow site to sync and publish content via the Webflow API.</p>
              </div>
              @if (webflowConnected) {
                <button
                  type="button"
                  class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm"
                  [disabled]="webflowLoading"
                  (click)="disconnectWebflow()"
                >
                  Disconnect
                </button>
              } @else {
                <button
                  type="button"
                  class="dk-btn dk-btn--success dk-btn--sm"
                  [disabled]="webflowLoading"
                  [attr.data-loading]="webflowLoading ? '' : null"
                  [attr.aria-busy]="webflowLoading"
                  (click)="connectWebflow()"
                >
                  {{ webflowLoading ? 'Connecting…' : 'Connect' }}
                </button>
              }
            </div>

            <div class="integration-row">
              <div class="integration-row__text">
                <p class="dk-body-sm dk-font-medium integration-row__title">
                  Slack
                  @if (slackConnected && slackChannelName) {
                    <span class="dk-caption">Posting to #{{ slackChannelName }}</span>
                  }
                </p>
                <p class="dk-caption">Get notified in Slack when requests are created or completed</p>
              </div>
              @if (slackConnected) {
                <div class="row-actions">
                  <button
                    type="button"
                    class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                    [disabled]="slackLoading"
                    (click)="openSlackSettings()"
                    aria-label="Slack notification settings"
                  >
                    <lucide-icon name="settings" [size]="16" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm"
                    [disabled]="slackLoading"
                    (click)="disconnectSlack()"
                  >
                    Disconnect
                  </button>
                </div>
              } @else if (canPickSlackChannel) {
                <button
                  type="button"
                  class="dk-btn dk-btn--success dk-btn--sm"
                  [disabled]="slackLoading"
                  (click)="openSlackSettings()"
                >
                  Connect
                </button>
              } @else {
                <button
                  type="button"
                  class="dk-btn dk-btn--success dk-btn--sm"
                  [disabled]="slackLoading"
                  [attr.data-loading]="slackLoading ? '' : null"
                  [attr.aria-busy]="slackLoading"
                  (click)="connectSlack()"
                >
                  {{ slackLoading ? 'Connecting…' : 'Connect' }}
                </button>
              }
            </div>

            <!-- Not released yet: hidden in production. -->
            @if (!isProd) {
              <div class="integration-row">
                <div class="integration-row__text">
                  <p class="dk-body-sm dk-font-medium integration-row__title">HubSpot</p>
                  <p
                    class="dk-caption integration-row__status"
                    [attr.data-broken]="hubspotState === 'broken' ? '' : null"
                  >
                    {{ hubspotCaption }}
                  </p>
                </div>
                @if (hubspotState === 'attached' || hubspotState === 'broken') {
                  @if (canManageHubspot) {
                    <div class="row-actions">
                      <button
                        type="button"
                        class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm"
                        [disabled]="hubspotLoading"
                        (click)="openHubspotSettings()"
                        aria-label="HubSpot settings"
                      >
                        <lucide-icon name="settings" [size]="16" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm"
                        [disabled]="hubspotLoading"
                        (click)="disconnectHubspot()"
                      >
                        Disconnect
                      </button>
                    </div>
                  }
                } @else {
                  <span [dkTooltip]="hubspotConnectBlockedReason">
                    <button
                      type="button"
                      class="dk-btn dk-btn--success dk-btn--sm"
                      [disabled]="hubspotLoading || hubspotConnecting || !!hubspotConnectBlockedReason"
                      [attr.data-loading]="hubspotConnecting ? '' : null"
                      [attr.aria-busy]="hubspotLoading || hubspotConnecting"
                      [attr.aria-label]="
                        hubspotConnectBlockedReason ? 'Connect HubSpot. ' + hubspotConnectBlockedReason : null
                      "
                      (click)="hubspotState === 'no-account' ? connectHubspot() : openHubspotSettings()"
                    >
                      {{ hubspotConnecting ? 'Connecting…' : 'Connect' }}
                    </button>
                  </span>
                }
              </div>
            }

            <!-- Absent for an organization without Google Chat, where Connect could only fail. -->
            @if (gchatEnabled) {
              <div class="integration-row">
                <div class="integration-row__text">
                  <p class="dk-body-sm dk-font-medium integration-row__title">Google Chat</p>
                  <p class="dk-caption">Get notified in Google Chat when requests are created or completed</p>
                </div>
                @if (gchatConnected) {
                  <button
                    type="button"
                    class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm"
                    [disabled]="gchatLoading"
                    (click)="disconnectGchat()"
                  >
                    Disconnect
                  </button>
                } @else if (!gchatPickerOpen) {
                  <button
                    type="button"
                    class="dk-btn dk-btn--success dk-btn--sm"
                    [disabled]="gchatLoading"
                    (click)="openGchatPicker()"
                  >
                    Connect
                  </button>
                }
              </div>

              @if (!gchatConnected && gchatPickerOpen) {
                <div class="gchat-picker">
                  @if (gchatSpacesLoading) {
                    <p class="dk-caption" role="status">Loading spaces…</p>
                  } @else {
                    <select
                      class="dk-field dk-field--select dk-field--sm gchat-picker__select"
                      aria-label="Google Chat space"
                      [(ngModel)]="gchatSelectedSpaceName"
                    >
                      <option value="">Select a space…</option>
                      @for (space of gchatSpaces; track space.name) {
                        <option [value]="space.name">{{ space.displayName }}</option>
                      }
                    </select>
                    <button
                      type="button"
                      class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--sm"
                      [disabled]="gchatConnecting"
                      (click)="cancelGchatPicker()"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="dk-btn dk-btn--sm"
                      [disabled]="!gchatSelectedSpaceName || gchatConnecting"
                      [attr.data-loading]="gchatConnecting ? '' : null"
                      [attr.aria-busy]="gchatConnecting"
                      (click)="connectGchat()"
                    >
                      {{ gchatConnecting ? 'Connecting…' : 'Connect' }}
                    </button>
                  }
                </div>
              }
            }
          </section>

          <!-- The same panel as Workspace → Agents, bound to this project: whatever is set
               here overrides the organization's, and whatever is left unset falls back to it. -->
          <div id="agentCredentialsSection">
            <app-agent-credentials
              [organizationId]="organizationId"
              [projectId]="selectedProjectId"
              [projectName]="selectedProjectName"
            />
          </div>

          <section class="settings-section">
            <div class="dk-card danger-zone">
              <h2 class="dk-heading-4">Danger zone</h2>

              <div class="danger-zone__row">
                <div>
                  <p class="dk-body-sm dk-font-medium">Leave project</p>
                  <p class="dk-caption">
                    Remove your account from this project. You'll lose access to all of its content.
                  </p>
                </div>
                <button
                  type="button"
                  class="dk-btn dk-btn--destructive dk-btn--outline dk-btn--sm"
                  [disabled]="isLeavingProject"
                  [attr.data-loading]="isLeavingProject ? '' : null"
                  [attr.aria-busy]="isLeavingProject"
                  (click)="openLeaveProjectConfirm()"
                >
                  Leave project
                </button>
              </div>

              @if (canDeleteProject) {
                <div class="danger-zone__row">
                  <div>
                    <p class="dk-body-sm dk-font-medium">Delete project</p>
                    <p class="dk-caption">Permanently delete this project and all sites in it.</p>
                  </div>
                  <button
                    type="button"
                    class="dk-btn dk-btn--destructive dk-btn--sm"
                    (click)="openDeleteProjectConfirm()"
                  >
                    Delete project
                  </button>
                </div>
              }
            </div>
          </section>
        }
      </div>
    </div>

    @if (slackSettingsOpen) {
      <div
        class="dk-modal-overlay"
        (click)="closeSlackSettings()"
        (keydown.escape)="closeSlackSettings(); $event.stopPropagation()"
      >
        <div
          class="dk-modal dk-modal--sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="slackSettingsTitle"
          aria-describedby="slackSettingsDescription"
          cdkTrapFocus
          [cdkTrapFocusAutoCapture]="true"
          (click)="$event.stopPropagation()"
        >
          <div class="dk-modal__header">
            <div>
              <h2 class="dk-modal__title" id="slackSettingsTitle">
                @if (selectedProjectName) {
                  Slack settings for "{{ selectedProjectName }}"
                } @else {
                  Slack settings
                }
              </h2>
              <p class="dk-modal__description" id="slackSettingsDescription">
                Choose the channel this project uses, and what gets posted there.
              </p>
            </div>
            <button
              type="button"
              class="dk-modal__close"
              aria-label="Close"
              [disabled]="slackConnecting"
              (click)="closeSlackSettings()"
            ></button>
          </div>

          <div class="dk-modal__body settings-dialog-body">
            @if (hasSeveralSlackWorkspaceLinks) {
              <div class="dk-form-group">
                <label class="dk-form-group__label" for="slackWorkspaceSelect">Workspace</label>
                <select
                  id="slackWorkspaceSelect"
                  class="dk-field dk-field--select"
                  [ngModel]="slackSelectedWorkspaceLinkId"
                  [disabled]="slackNotificationsDisabled || slackConnecting"
                  (ngModelChange)="selectSlackWorkspaceLink($event)"
                >
                  <option value="">Select a workspace…</option>
                  @for (link of slackWorkspaceLinks; track link.id) {
                    <option [value]="link.id">{{ link.team_name }}</option>
                  }
                </select>
              </div>
            }

            <div class="dk-form-group">
              <label class="dk-form-group__label" for="slackChannelSelect">Channel</label>
              @if (slackChannelsLoading) {
                <p class="dk-form-group__hint" role="status">Loading channels…</p>
              } @else {
                <select
                  id="slackChannelSelect"
                  class="dk-field dk-field--select"
                  [(ngModel)]="slackSelectedChannelId"
                  [disabled]="slackNotificationsDisabled || slackConnecting || !slackChannels.length"
                  [attr.aria-describedby]="
                    !slackChannels.length && hasSeveralSlackWorkspaceLinks && !slackSelectedWorkspaceLinkId
                      ? 'slackChannelHint'
                      : null
                  "
                >
                  <option value="">Select a channel…</option>
                  @for (channel of slackChannels; track channel.id) {
                    <option [value]="channel.id">
                      {{ channel.is_private ? '&#128274; ' : '# ' }}{{ channel.name }}
                    </option>
                  }
                </select>
                @if (!slackChannels.length && hasSeveralSlackWorkspaceLinks && !slackSelectedWorkspaceLinkId) {
                  <p class="dk-form-group__hint" id="slackChannelHint">Pick a workspace first.</p>
                }
              }
            </div>

            <fieldset class="dk-form-group" [disabled]="slackNotificationsDisabled || slackConnecting">
              <legend class="dk-form-group__label">Post a message when</legend>
              <label class="dk-check">
                <input class="dk-checkbox" type="checkbox" [(ngModel)]="slackNotifyOnCreation" />
                <span>A request is created</span>
              </label>
              <label class="dk-check">
                <input class="dk-checkbox" type="checkbox" [(ngModel)]="slackNotifyOnCompletion" />
                <span>A request is completed</span>
              </label>
            </fieldset>

            <hr class="dk-divider" />

            <label class="dk-check">
              <input
                class="dk-checkbox"
                type="checkbox"
                [(ngModel)]="slackNotificationsDisabled"
                [disabled]="slackConnecting"
              />
              <span>Disable Slack notifications on this project</span>
            </label>

            <div class="dk-alert dk-alert--info">
              <div class="dk-alert__body">
                <p>
                  Organization members will be able to open a request for this project with
                  <code class="settings-code">/dk-request</code>.
                </p>
              </div>
            </div>
          </div>

          <div class="dk-modal__footer">
            <button
              type="button"
              class="dk-btn dk-btn--secondary"
              [disabled]="slackConnecting"
              (click)="closeSlackSettings()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="dk-btn"
              [disabled]="!canSaveSlackSettings || slackConnecting"
              [attr.data-loading]="slackConnecting ? '' : null"
              [attr.aria-busy]="slackConnecting"
              (click)="saveSlackChannel()"
            >
              {{ slackConnecting ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    }

    @if (deleteProjectConfirmOpen) {
      <div
        class="dk-modal-overlay"
        (click)="closeDeleteProjectConfirm()"
        (keydown.escape)="closeDeleteProjectConfirm(); $event.stopPropagation()"
      >
        <div
          class="dk-modal dk-modal--sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="deleteProjectTitle"
          aria-describedby="deleteProjectDescription"
          cdkTrapFocus
          [cdkTrapFocusAutoCapture]="true"
          (click)="$event.stopPropagation()"
        >
          <div class="dk-modal__header">
            <div>
              <div class="dk-modal__icon dk-modal__icon--destructive" aria-hidden="true">
                <lucide-icon name="alert-triangle" [size]="16" />
              </div>
              <h2 class="dk-modal__title" id="deleteProjectTitle">Delete "{{ selectedProjectName }}"?</h2>
            </div>
            <button
              type="button"
              class="dk-modal__close"
              aria-label="Close"
              [disabled]="isDeletingProject"
              (click)="closeDeleteProjectConfirm()"
            ></button>
          </div>

          <div class="dk-modal__body settings-dialog-body">
            <p class="dk-body-sm" id="deleteProjectDescription">
              This action <strong>cannot be undone</strong>. It permanently deletes the project, every member's access,
              all request tickets, sites and pages, and any connected integrations.
            </p>
            <div class="dk-form-group">
              <label class="dk-form-group__label" for="deleteProjectConfirm"
                >Type <strong>{{ selectedProjectName }}</strong> to confirm.</label
              >
              <input
                class="dk-field"
                type="text"
                id="deleteProjectConfirm"
                [ngModel]="deleteProjectConfirmText"
                (ngModelChange)="deleteProjectConfirmText = $event"
                [placeholder]="selectedProjectName"
                autocomplete="off"
                [disabled]="isDeletingProject"
                [attr.aria-invalid]="deleteProjectError ? 'true' : null"
                [attr.aria-describedby]="deleteProjectError ? 'deleteProjectError' : null"
              />
              @if (deleteProjectError) {
                <p class="dk-form-group__error" id="deleteProjectError" role="alert">{{ deleteProjectError }}</p>
              }
            </div>
          </div>

          <div class="dk-modal__footer">
            <button
              type="button"
              class="dk-btn dk-btn--secondary"
              [disabled]="isDeletingProject"
              (click)="closeDeleteProjectConfirm()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="dk-btn dk-btn--destructive"
              [disabled]="!canConfirmDeleteProject || isDeletingProject"
              [attr.data-loading]="isDeletingProject ? '' : null"
              [attr.aria-busy]="isDeletingProject"
              (click)="confirmDeleteProject()"
            >
              {{ isDeletingProject ? 'Deleting…' : 'Delete project' }}
            </button>
          </div>
        </div>
      </div>
    }
  </div>
}
